import { type ReactNode, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import {
  useConnectSequencerKeyMutation,
  useDisconnectSequencerMutation,
  useStartOutreachConnectMutation,
} from '@/store/features/sequencers.feature';
import type { SequenceProvider } from '@/types/custom-playbooks';
import { ConnectorLogo } from '@/containers/playbooks/custom-builder/connector-logo.component';
import { SEQUENCE_PROVIDER_NAMES } from '@/containers/playbooks/custom-builder/sequence-step.util';
import { serverReason } from '@/containers/agents/server-reason.util';
import {
  ApiKeyConnectDialog,
  KeyHelpLink,
} from './api-key-connect-dialog.component';
import {
  ConnectButton,
  DESTRUCTIVE_ITEM,
  ManageMenu,
} from './integration-card.component';

const INSTANTLY_API_KEYS_URL =
  'https://app.instantly.ai/app/settings/integrations?section=api-keys';
const SALESLOFT_API_KEYS_URL =
  'https://accounts.salesloft.com/oauth/applications';

/** Where each provider's key lives. Outreach signs in instead of a key. */
const KEY_HELP: Record<EngagementProvider, ReactNode> = {
  salesloft: (
    <>
      Create one in{' '}
      <KeyHelpLink href={SALESLOFT_API_KEYS_URL}>
        Salesloft &rarr; Your Applications &rarr; API Keys
      </KeyHelpLink>{' '}
      with Create New. Give it the scopes for people, cadences, cadence
      memberships, steps and users.
    </>
  ),
  nooks: (
    <>
      Find it in Nooks &rarr; Developer Settings &rarr; API Keys. Nooks adds
      only people already in Salesforce or HubSpot, so put a CRM step first.
    </>
  ),
  instantly: (
    <>
      Create one in{' '}
      <KeyHelpLink href={INSTANTLY_API_KEYS_URL}>
        Instantly &rarr; Settings &rarr; Integrations &rarr; API Keys
      </KeyHelpLink>{' '}
      with the all:read and leads:create scopes.
    </>
  ),
  outreach: null,
};

/** A provider's mark: its own logo, Cleanlist's icon in each theme. */
export function SequencerLogo({
  provider,
  className,
}: {
  provider: SequenceProvider;
  className?: string;
}) {
  return <ConnectorLogo id={provider} className={className} />;
}

/** The outside sequencers with a card of their own here (lemlist is a CRM
 *  card, on `/crm/lemlist/connect`). */
export type EngagementProvider =
  | 'salesloft'
  | 'outreach'
  | 'nooks'
  | 'instantly';
/**
 * Connect an outside sequencer, from its Integrations card only: Salesloft,
 * Nooks and Instantly with an API key (the key goes straight to the server,
 * which checks it with a read), Outreach through its sign in, in a new tab.
 * A refused key keeps the dialog open with the server's reason (a bad key, no
 * paid plan, the scopes it lacks). `onConnected` runs after a key connects,
 * and when the tab comes back into focus after an Outreach sign in, so the
 * caller reads the connection again. `trigger` draws the caller's own button
 * in place of the card's (Clu's connection card).
 */
export function SequencerConnectButton({
  orgId,
  provider,
  onConnected,
  trigger,
}: {
  orgId: string;
  provider: EngagementProvider;
  onConnected: () => void;
  trigger?: (connect: () => void, busy: boolean) => ReactNode;
}) {
  const name = SEQUENCE_PROVIDER_NAMES[provider];
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [awaitingOutreach, setAwaitingOutreach] = useState(false);
  // Closing the dialog ends its attempt, so a key still being checked then
  // can't put its refusal into the next opening.
  const attempt = useRef(0);
  // Each key goes to its own provider's route. Outreach signs in instead and
  // never submits a key, so its value here is unused.
  const [connectKey, keyed] = useConnectSequencerKeyMutation(
    orgId,
    provider === 'outreach' ? 'salesloft' : provider
  );
  const [startOutreach, outreach] = useStartOutreachConnectMutation();
  const saving = keyed.isLoading;

  useEffect(() => {
    if (!awaitingOutreach) return;
    const back = () => {
      setAwaitingOutreach(false);
      onConnected();
    };
    window.addEventListener('focus', back);
    return () => window.removeEventListener('focus', back);
  }, [awaitingOutreach, onConnected]);

  const submit = async () => {
    const trimmed = key.trim();
    if (!trimmed || saving) return;
    setError(null);
    const mine = attempt.current;
    try {
      await connectKey(trimmed).unwrap();
      setKey('');
      setOpen(false);
      onConnected();
      toast.success(`${name} connected.`);
    } catch (refused) {
      if (mine !== attempt.current) return;
      setError(serverReason(refused, `Couldn’t connect ${name}. Try again.`));
    }
  };

  const connect = async () => {
    if (provider !== 'outreach') {
      setOpen(true);
      return;
    }
    // Open the tab inside the click, before the request, so popup blockers
    // (Safari) still treat it as user initiated. It goes to the sign in once
    // the URL is back, and closes if the request fails.
    const tab = window.open('', '_blank');
    try {
      const { url } = await startOutreach({ orgId }).unwrap();
      if (tab) {
        tab.opener = null;
        tab.location.href = url;
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
      setAwaitingOutreach(true);
    } catch {
      tab?.close();
      toast.error('Couldn’t start the Outreach sign in');
    }
  };

  return (
    <>
      {trigger ? (
        trigger(() => void connect(), outreach.isLoading)
      ) : (
        <ConnectButton
          name={name}
          busy={outreach.isLoading}
          onClick={() => void connect()}
        />
      )}
      <ApiKeyConnectDialog
        name={name}
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            attempt.current += 1;
            setKey('');
            setError(null);
          }
        }}
        help={KEY_HELP[provider]}
        value={key}
        onValueChange={(value) => {
          setKey(value);
          setError(null);
        }}
        onSubmit={() => void submit()}
        busy={saving}
        cancelWhileBusy
        error={error}
      />
    </>
  );
}

/** A connected sequencer's Disconnect, behind a confirm: it is the org's one
 *  connection, so plays that add to it stop until it is connected again. */
export function SequencerDisconnectItem({
  orgId,
  provider,
}: {
  orgId: string;
  provider: EngagementProvider;
}) {
  const name = SEQUENCE_PROVIDER_NAMES[provider];
  const [confirm, setConfirm] = useState(false);
  const [disconnect, { isLoading }] = useDisconnectSequencerMutation();
  return (
    <>
      <ManageMenu name={name}>
        <DropdownMenuItem
          className={DESTRUCTIVE_ITEM}
          disabled={isLoading}
          onSelect={() => setConfirm(true)}
        >
          Disconnect
        </DropdownMenuItem>
      </ManageMenu>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect {name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Plays that add people to {name} stop at that step until you
              connect it again. It disconnects {name} for your whole
              organization.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                disconnect({ orgId, provider })
                  .unwrap()
                  .then(() => toast.success(`${name} disconnected.`))
                  .catch(() => toast.error(`Couldn’t disconnect ${name}`));
              }}
            >
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
